import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidV4 } from 'uuid';
import { omit } from 'lodash-es';
import { AdminPageHeader, Form, AdminFormAction } from '@ui';
import { NewHubForm } from '@lp/features/admin/forms/HubForm/types';
import { DEFAULT_HUB_VALUES, useHubFormSchema } from '@lp/features/admin/forms/HubForm/config';
import { HubForm } from '@lp/features/admin/forms/HubForm/components/HubForm';
import { useContext } from 'react';
import { AuthContext } from '@lp/core/auth';
import { useCreateHubMutation } from '@/queries/hub/hub.mutation';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { convertToIanaTimezone } from '@/utils/helpers';

export const HubCreate = () => {
  const navigate = useNavigate();
  const { mutateAsync } = useCreateHubMutation();
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.NopHubCreate);
  const { schema, isLoading: isGeoDataLoading } = useHubFormSchema();

  const handleBackToList = () => {
    navigate('/admin/hubs/locations');
  };

  const handleSubmit = async (data: NewHubForm) => {
    try {
      const values = {
        ...omit(data, ['regionalManagerName', 'nationalManagerName', 'timezone']),
        nationalManagerId: data.nationalManagerId || null,
        regionalManagerId: data.regionalManagerId || null,
        timezone: data.timezoneIANA || convertToIanaTimezone(data.timezone),
      };
      await mutateAsync({
        id: uuidV4(),
        ...values,
      });

      toast.success(`Hub created successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  if (isGeoDataLoading) return null;

  return (
    <Form<NewHubForm> defaultValues={DEFAULT_HUB_VALUES} schema={schema} onSubmit={handleSubmit}>
      <div className="flex flex-col gap-9">
        <AdminPageHeader title="Create New NOP Hub" backToListLink="/admin/hubs/locations">
          <AdminFormAction type="create" handleClick={handleBackToList} hasCreatePermissions={hasCreatePermissions} />
        </AdminPageHeader>
        <HubForm isReadOnly={!hasCreatePermissions} />
      </div>
    </Form>
  );
};
